/**
 * OverloadSkillEffect — Lightning tree Discharge path evolution (replaces Discharge).
 *
 * Instead of dumping charge instantly, activating Overload enters an 8-second
 * timed mode where: (1) charge builds at 2× the normal rate, and (2) every
 * attack bleeds stored charge as bonus Lightning damage. When the mode expires
 * (it cannot be cancelled early), all remaining charge detonates in a burst
 * identical to a full Discharge — Surge's stun and radius multiplier apply.
 *
 * Cooldown begins after the mode ends, not at activation.
 *
 * The integration layer reads `chargeRateMultiplier` each frame and scales
 * the ChargeSystem's accumulation input accordingly. `onAttackHit()` is called
 * by the integration layer whenever the player lands a hit during Overload mode;
 * it consumes charge and returns the bonus Lightning damage to apply.
 *
 * Upgrade keys (via resolver, scope: 'discharge'):
 * - radiusMultiplier: multiplicative — terminal burst radius scaling (Surge: 1.3x)
 * - stunDurationMs: additive — stun applied to targets in terminal burst (Surge: 300ms)
 * - trailDurationMs: additive — charge_trail zone duration (Trail: 2000ms)
 * - trailDamage: additive — damage dealt to enemies entering the trail (Trail: 4)
 * - trailStaggerMs: additive — stagger applied on trail contact (Trail: 250ms)
 *
 * Trail hooks into dashing, not Overload activation — same behavior as Discharge.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see lightning-spec.md section Overload (`overload`)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces (shared with Discharge for burst reuse)
// ---------------------------------------------------------------------------

export interface OverloadAoEConfig {
  readonly radiusTiles: number;
  readonly damageMultiplier: number;
  readonly sourceSkillId: string;
}

export interface OverloadAoEResult {
  readonly enemiesHit: number;
}

export interface OverloadTrailZoneConfig {
  readonly type: 'charge_trail';
  readonly durationMs: number;
  readonly entryDamage: number;
  readonly entryStaggerMs: number;
  readonly sourceSkillId: string;
}

export interface OverloadDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly aoe: {
    burst(config: OverloadAoEConfig): OverloadAoEResult;
  };
  readonly charge: {
    /** Consume charge — amount undefined = consume all, number = consume up to that amount. */
    consume(chargeId: string, amount?: number): number;
    /** Read current charge state for ratio calculations. */
    get(chargeId: string): { readonly current: number; readonly maxCharge: number } | undefined;
  };
  readonly statusEffects: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    isImmune(defId: string): boolean;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
  /** Fired after terminal detonation — Afterburn and Spent listeners receive this. */
  readonly onDetonation?: () => void;
  /** Zone system — required when Trail upgrade is unlocked. */
  readonly zones?: {
    create(config: OverloadTrailZoneConfig): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface OverloadConfig {
  /** Medium cooldown tier: 1200ms. Begins after the 8-second mode ends. */
  readonly cooldownMs: number;
  /** Duration of Overload mode in ms. */
  readonly baseDurationMs: number;
  /** Charge consumed per attack hit during Overload mode. */
  readonly chargeBleedPerHit: number;
  /** Multiplier applied to bled charge to compute bonus Lightning damage. */
  readonly chargeToBonusDamage: number;
  /** Charge accumulation rate multiplier during Overload mode. */
  readonly chargeRateMultiplier: number;
  /** Maximum charge capacity — used for terminal detonation ratio. */
  readonly maxCharge: number;
  /** Base blast radius in tiles at zero charge (terminal detonation). */
  readonly baseRadiusTiles: number;
  /** Additional radius added at full charge (terminal detonation). */
  readonly additionalRadiusTiles: number;
  /** Base damage multiplier at zero charge (terminal detonation). */
  readonly baseDamageMultiplier: number;
  /** Additional damage multiplier added at full charge (terminal detonation). */
  readonly additionalDamageMultiplier: number;
}

const DEFAULT_CONFIG: OverloadConfig = {
  cooldownMs: 1200,
  baseDurationMs: 8000,
  chargeBleedPerHit: 10,
  chargeToBonusDamage: 1.0,
  chargeRateMultiplier: 2.0,
  maxCharge: 100,
  baseRadiusTiles: 1.0,
  additionalRadiusTiles: 2.0,
  baseDamageMultiplier: 0.2,
  additionalDamageMultiplier: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class OverloadSkillEffect implements SkillEffect {
  readonly skillId = 'overload';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: OverloadDeps;
  private readonly config: OverloadConfig;

  private _cooldownRemaining = 0;

  // Mode state — reset on each activation.
  private _isActive = false;
  private _modeElapsed = 0;

  // Terminal detonation tracking (set during _detonate).
  private _lastChargeConsumed = 0;
  private _lastChargeRatio = 0;
  private _lastBurstRadius = 0;
  private _lastDamageMultiplier = 0;

  // Lifetime stats.
  private _activations = 0;
  private _fizzles = 0;
  private _detonations = 0;
  private _totalChargeBled = 0;
  private _totalBonusDamage = 0;
  private _attackHitsProcessed = 0;
  private _stunApplications = 0;
  private _trailsSpawned = 0;

  constructor(deps: OverloadDeps, config: Partial<OverloadConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // -- Read-only accessors for simulation inspection --------------------------

  get cooldown(): number { return this._cooldownRemaining; }
  get isOverloadActive(): boolean { return this._isActive; }
  get modeElapsed(): number { return this._modeElapsed; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get detonations(): number { return this._detonations; }
  get totalChargeBled(): number { return this._totalChargeBled; }
  get totalBonusDamage(): number { return this._totalBonusDamage; }
  get attackHitsProcessed(): number { return this._attackHitsProcessed; }
  get stunApplications(): number { return this._stunApplications; }
  get lastChargeConsumed(): number { return this._lastChargeConsumed; }
  get lastChargeRatio(): number { return this._lastChargeRatio; }
  get lastBurstRadius(): number { return this._lastBurstRadius; }
  get lastDamageMultiplier(): number { return this._lastDamageMultiplier; }
  get trailsSpawned(): number { return this._trailsSpawned; }

  /**
   * Charge rate multiplier — the integration layer reads this each frame
   * and scales ChargeSystem accumulation accordingly. Returns 2.0 during
   * Overload mode, 1.0 otherwise.
   */
  get chargeRateMultiplier(): number {
    return this._isActive ? this.config.chargeRateMultiplier : 1.0;
  }

  // -- Trail (dash-triggered zone) --------------------------------------------

  /**
   * Called by the integration layer whenever the player dashes. If Trail is
   * unlocked (trailDurationMs > 0 via resolver), spawns a charge_trail zone
   * along the dash path. Inherited from Discharge — independent of Overload.
   */
  onDash(): void {
    const trailDurationMs = this.deps.resolver.getAdditiveEffect('discharge', 'trailDurationMs');
    if (trailDurationMs <= 0) return;
    if (!this.deps.zones) return;

    const trailDamage = this.deps.resolver.getAdditiveEffect('discharge', 'trailDamage');
    const trailStaggerMs = this.deps.resolver.getAdditiveEffect('discharge', 'trailStaggerMs');

    this.deps.zones.create({
      type: 'charge_trail',
      durationMs: trailDurationMs,
      entryDamage: trailDamage,
      entryStaggerMs: trailStaggerMs,
      sourceSkillId: 'overload',
    });

    this._trailsSpawned++;
  }

  // -- Attack hit handler (charge bleed) --------------------------------------

  /**
   * Called by the integration layer when the player lands any attack hit
   * during Overload mode. Consumes `chargeBleedPerHit` from static_charge
   * and returns the bonus Lightning damage to add to the hit.
   *
   * Returns 0 if Overload is not active or no charge is available.
   */
  onAttackHit(): number {
    if (!this._isActive) return 0;

    const consumed = this.deps.charge.consume('static_charge', this.config.chargeBleedPerHit);
    if (consumed <= 0) return 0;

    const bonusDamage = consumed * this.config.chargeToBonusDamage;
    this._totalChargeBled += consumed;
    this._totalBonusDamage += bonusDamage;
    this._attackHitsProcessed++;

    return bonusDamage;
  }

  // -- SkillEffect interface --------------------------------------------------

  canActivate(): boolean {
    return !this._isActive && this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle — skill attempt failed, no mode entered, no cooldown.
    if (!this.deps.rollSkillSuccess('overload')) {
      this._fizzles++;
      return false;
    }

    this.deps.breakHide?.();

    // Enter timed mode.
    this._isActive = true;
    this._modeElapsed = 0;

    // Cooldown does NOT start here — it starts after mode ends.
    this._activations++;
    return true;
  }

  /**
   * Overload cannot be manually cancelled — it runs its full 8-second
   * duration before detonating. Intentional no-op.
   */
  deactivate(): void {
    // Intentionally empty.
  }

  isActive(): boolean {
    return this._isActive;
  }

  update(delta: number): void {
    // Tick cooldown.
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick mode timer.
    if (this._isActive) {
      this._modeElapsed += delta;

      // Mode expired — terminal detonation.
      if (this._modeElapsed >= this.config.baseDurationMs) {
        this._detonate();
        this._isActive = false;
        this._cooldownRemaining = this.config.cooldownMs;
      }
    }
  }

  destroy(): void {
    if (this._isActive) {
      this._isActive = false;
    }
    this._cooldownRemaining = 0;
  }

  // -- Terminal detonation (replicates Discharge burst logic) ------------------

  private _detonate(): void {
    // 1. Consume all remaining static charge.
    const consumed = this.deps.charge.consume('static_charge');
    this._lastChargeConsumed = consumed;

    // 2. Compute charge ratio.
    const maxCharge = this.config.maxCharge;
    const ratio = maxCharge > 0 ? Math.min(consumed / maxCharge, 1.0) : 0;
    this._lastChargeRatio = ratio;

    // 3. Compute charge-scaled radius with Surge multiplier.
    const baseRadius = this.config.baseRadiusTiles + ratio * this.config.additionalRadiusTiles;
    const radiusMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'discharge', 'radiusMultiplier',
    );
    this._lastBurstRadius = baseRadius * radiusMultiplier;

    // 4. Compute charge-scaled damage multiplier.
    this._lastDamageMultiplier =
      this.config.baseDamageMultiplier + ratio * this.config.additionalDamageMultiplier;

    // 5. Fire AoE burst centered on player.
    const result = this.deps.aoe.burst({
      radiusTiles: this._lastBurstRadius,
      damageMultiplier: this._lastDamageMultiplier,
      sourceSkillId: 'overload',
    });

    // 6. Apply stun to hit targets if Surge is unlocked (stunDurationMs > 0).
    if (result.enemiesHit > 0) {
      const stunMs = this.deps.resolver.getAdditiveEffect('discharge', 'stunDurationMs');
      if (stunMs > 0 && !this.deps.statusEffects.isImmune('stun')) {
        this.deps.statusEffects.apply('stun', {
          sourceSkillId: 'overload',
          durationMs: stunMs,
        });
        this._stunApplications++;
      }
    }

    // 7. Fire detonation event for Afterburn / Spent / downstream listeners.
    this._detonations++;
    this.deps.onDetonation?.();
  }
}
